import { useEffect, useState } from 'react';
import { isWebKitOnly } from '@/components/effects/useVoidRender';

/**
 * The hero "gravity well".
 *
 * Original (Chromium/Firefox): an HTML div with a radial gradient warped by
 * the #void-warp SVG filter (feTurbulence + feDisplacementMap) and blurred
 * 3px. Rendered here byte-for-byte as it always was.
 *
 * WebKit (Safari + every iOS browser) cannot apply that SVG filter to an
 * HTML element and drops the layer entirely. It CAN apply the very same
 * filter to SVG content — so on WebKit we draw the identical gradient on an
 * SVG <rect> and run the identical turbulence/displacement chain (with the
 * 3px blur appended inside the filter). Same math, same smoke.
 */

const ORIGINAL_BACKGROUND = 'radial-gradient(circle max(42vmin, 28vh) at 50% 50%, #0A0A0F 0%, #0A0A0F 48%, rgba(10,10,15,0.95) 55%, rgba(10,10,15,0.75) 65%, rgba(10,10,15,0.4) 76%, rgba(10,10,15,0.12) 87%, transparent 100%)';

// Mirrors the CSS gradient stops above (offset → opacity of #0A0A0F)
const STOPS = [
  [0, 1], [0.48, 1], [0.55, 0.95], [0.65, 0.75], [0.76, 0.4], [0.87, 0.12], [1, 0],
];

function useViewport() {
  const get = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [vp, setVp] = useState(get);
  useEffect(() => {
    let raf = 0;
    const onResize = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setVp(get())); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); };
  }, []);
  return vp;
}

function WebKitVoid() {
  const { w, h } = useViewport();
  // CSS: circle max(42vmin, 28vh)
  const r = Math.max(0.42 * Math.min(w, h), 0.28 * h);
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ width: '100vw', height: '100vh', display: 'block' }}
      aria-hidden="true"
      data-testid="void-svg"
    >
      <defs>
        <radialGradient id="void-grad" gradientUnits="userSpaceOnUse" cx={w / 2} cy={h / 2} r={r}>
          {STOPS.map(([o, a]) => (
            <stop key={o} offset={o} stopColor="#0A0A0F" stopOpacity={a} />
          ))}
        </radialGradient>
        <filter id="void-warp-svg" x="-25%" y="-25%" width="150%" height="150%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="4" seed="3" stitchTiles="stitch" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="8" result="smoothNoise" />
          <feDisplacementMap in="SourceGraphic" in2="smoothNoise" scale="50" xChannelSelector="R" yChannelSelector="G" result="warped" />
          <feGaussianBlur in="warped" stdDeviation="3" />
        </filter>
      </defs>
      <rect x="0" y="0" width={w} height={h} fill="url(#void-grad)" filter="url(#void-warp-svg)" />
    </svg>
  );
}

export default function VoidLayer() {
  const [webkit] = useState(() => isWebKitOnly());
  if (webkit) return <WebKitVoid />;
  return (
    <div style={{
      width: '100vw', height: '100vh',
      background: ORIGINAL_BACKGROUND,
      filter: 'url(#void-warp) blur(3px)',
    }} />
  );
}
